import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyCheckDollar,
  faKey,
  faThumbsUp,
  faTag,
  faStar,
  faTrophy,
  faTicket,
} from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
  faSteam as fabFaSteam,
} from '@fortawesome/free-brands-svg-icons';

import BundleTimer from 'Components/Timers/BundleTimer';
import DangerousHTML from 'Components/Utility/DangerousHTML';
import imgixConfig from 'Config/imgix';
import ItemList from 'Components/Utility/SEO/ItemList';
import MultiDrm from 'Components/Utility/MultiDrm';
import PaymentCards from 'Components/PaymentCards/PaymentCards';
import Price from 'Components/Utility/Price/Price';
import ProductAddToCartBtn from 'Components/ProductAddToCartBtn/ProductAddToCartBtn';
import ProductBreadcrumbs from 'Components/Utility/SEO/ProductBreadcrumbs';
import ProductCover from 'Components/Utility/ProductCover';
import ProductOffers from 'Components/ProductOffers/ProductOffers';
import ProductRecommendations from 'Components/ProductRecommendations/ProductRecommendations';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import ProductSEOHelmet from 'Components/Utility/SEO/ProductSEOHelmet';
import ProductStructuredData from 'Components/Utility/SEO/ProductStructuredData';
import RegionLocked from 'Components/Regional/RegionLocked';
import SelectedTierContext from 'Features/Bundle/context/selected-tier-context';
import TemplateSelectorGameBundle from 'Features/Bundle/components/Templates/GameTemplates/GameBundleTemplateSelector';
import useViewport from 'Hooks/useViewport';
import WishlistButton from 'Components/Wishlist/WishlistButton';
import env from 'Env';
import MysteryKeySelectionButtons from 'Features/Bundle/components/Mystery/MysteryKeySelectionButtons';
import { IsControl } from 'Redux/ducks/split-test';

import 'Features/Bundle/components/Layout/MysteryBundleView.scss';

const messages = defineMessages({
  offers_title: {
    id: 'mystery-bundle-page.offers-title',
    defaultMessage: 'Offers and Promotions',
  },
  rec_header: {
    id: 'mystery-bundle-page.recommendations',
    defaultMessage: 'Other products you may like',
  },
});

const MysteryBundleView = ({ product }) => {
  const { formatMessage } = useIntl();
  const { selectTier, inCartTier, selectedTier } = useContext(SelectedTierContext);

  const { isAboveMd, isBelowMd } = useViewport();

  const bundles = get(product, 'bundles', []);
  const cover = get(product, 'cover', '');
  const displayType = get(product, 'display_type', '');
  const drm = get(product, 'drm', {});
  const price = get(product, 'price', {});
  const drmEditions = get(product, 'drmEditions', []);
  const excludedRegions = get(product, 'regions_excluded', []);
  const includedRegions = get(product, 'regions_included', []);
  const name = get(product, 'name', '');
  const smallprint = get(product, 'notice.legal', '');
  const validUntil = get(product, 'availability.valid_until', null);
  const ztormId = get(product, 'ztormId', null);
  const slug = get(product, 'slug', '');
  const productId = get(product, '_id', '');
  const topImage = get(product, 'bundleCover', '');
  const description = get(product, 'desc', '');
  const templateType = get(product, 'template_type', '');

  const currentDrm = ProductRequirements.CurrentDrm(product);
  const validPlatforms = ProductRequirements.validPlatforms(product);

  const useNewComponents = false;

  let totalGameCount = 0;

  const bundleTiers = cloneDeep(bundles);
  bundleTiers.forEach((tier) => {
    sortBy(tier.games, ['sort_order']).filter(game => game.display).forEach((game) => {
      totalGameCount += 1;
      game.totalGameIndex = totalGameCount; // eslint-disable-line no-param-reassign
    });
  });

  return (
    <main className="MysteryBundlePage container">
      <ProductBreadcrumbs product={product} />
      <ProductSEOHelmet product={product} />
      <ProductStructuredData product={product} />
      <ItemList noLinks title="Games in this Bundle" hits={product.bundles.reduce((acc, tier) => acc.concat(tier.games), [])} />

      <header className="MysteryBundlePage__header">
        <section className="MysteryBundlePage__header__titleTimer">
          <h1 className="MysteryBundlePage__header__titleTimer__heading">
            {name}
          </h1>

          {isAboveMd && validUntil &&
            <BundleTimer value={Date.parse(validUntil)} />
          }
        </section>

        <section className="MysteryBundlePage__header__main">
          <div className="MysteryBundlePage__header__cover">
            <ProductCover
              cover={cover}
              alt={`Product cover for ${name}`}
              displayType={displayType}
              sizesOverride="(min-width: 1520px) 662px, (min-width: 1200px) 562px, (min-width: 991px) 472px, (min-width: 768px) 704px, (min-width: 576px) 524px, 98vw"
            />
          </div>
          <div className="MysteryBundlePage__header__details">
            <div className="MysteryBundlePage__header__details__icons">
              {isEmpty(drmEditions) && currentDrm &&
                <MultiDrm drm={drm} />
              }

              <div className="MysteryBundlePage__header__details__icons__platforms">
                <span>
                  <FontAwesomeIcon icon={fabFaWindows} />
                </span>
                {includes(validPlatforms, 'mac') &&
                  <span>
                    <FontAwesomeIcon icon={fabFaApple} />
                  </span>
                }
                {includes(validPlatforms, 'linux') &&
                  <span>
                    <FontAwesomeIcon icon={fabFaLinux} />
                  </span>
                }
              </div>
            </div>

            {templateType === 'standard_mystery' &&
              <div className="MysteryBundlePage__header__price">
                <FormattedMessage
                  id="mystery-bundle-page.standard-mystery"
                  defaultMessage="Get {totalGameCount} games for {price}"
                  values={{
                    price: <Price price={price} />,
                    totalGameCount,
                  }}
                />
              </div>
            }

            {templateType === 'tiered_mystery' &&
              <MysteryKeySelectionButtons bundles={bundles} selectTier={selectTier} />
            }

            <div className="MysteryBundlePage__header__details__buttons">
              <ProductAddToCartBtn
                oldStyle
                product={product}
                selectedTier={selectedTier}
                inCartTier={inCartTier}
              />

              <WishlistButton
                bundleButton
                slug={slug}
                product={product}
                productId={productId}
              />
            </div>

            <RegionLocked included={includedRegions} excluded={excludedRegions} ztormId={ztormId} />

            <PaymentCards />
          </div>
        </section>
      </header>

      {IsControl('MysteryBundleSellingPointsRedesign') ? (
        <section className="MysteryBundlePage__sellingPoints">
          <div className="MysteryBundlePage__sellingPoints__point">
            <div className="MysteryBundlePage__sellingPoints__point__icon">
              <FontAwesomeIcon icon={faMoneyCheckDollar} />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__title">
              1M+
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__text">
              <FormattedMessage id="mystery-bundle-page.selling-point.one.text" defaultMessage="Mystery keys sold" />
            </div>
          </div>

          <div className="MysteryBundlePage__sellingPoints__point">
            <div className="MysteryBundlePage__sellingPoints__point__icon">
              <FontAwesomeIcon icon={faStar} />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__title">
              <FormattedMessage id="mystery-bundle-page.selling-point.two.title" defaultMessage="Very Positive" />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__text">
              <FormattedMessage id="mystery-bundle-page.selling-point.two.text" defaultMessage="Average Steam rating" />
            </div>
          </div>

          <div className="MysteryBundlePage__sellingPoints__point">
            <div className="MysteryBundlePage__sellingPoints__point__icon">
              <FontAwesomeIcon icon={faTrophy} />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__title">
              <FormattedMessage id="mystery-bundle-page.selling-point.three.title" defaultMessage="Redeem on Steam" />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__text">
              <FormattedMessage id="mystery-bundle-page.selling-point.three.text" defaultMessage="Officially licensed games" />
            </div>
          </div>

          <div className="MysteryBundlePage__sellingPoints__point">
            <div className="MysteryBundlePage__sellingPoints__point__icon">
              <FontAwesomeIcon icon={faTicket} />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__title">
              <FormattedMessage id="mystery-bundle-page.selling-point.four.title" defaultMessage="5% off next order" />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__text">
              <FormattedMessage id="mystery-bundle-page.selling-point.four.text" defaultMessage="When you buy this product" />
            </div>
          </div>
        </section>
      ) : (
        <section className="MysteryBundlePage__sellingPoints__challenger">
          <div className="MysteryBundlePage__sellingPoints__point">
            <div className="MysteryBundlePage__sellingPoints__point__icon">
              <FontAwesomeIcon icon={faKey} />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__body">
              <div className="MysteryBundlePage__sellingPoints__point__title">
                <FormattedMessage id="mystery-bundle-page.selling-point-challenger.one.title" defaultMessage="1M+ Keys Sold" />
              </div>
              <div className="MysteryBundlePage__sellingPoints__point__text">
                <FormattedMessage id="mystery-bundle-page.selling-point-challenger.one.text" defaultMessage="Across our Bundles" />
              </div>
            </div>
          </div>

          <div className="MysteryBundlePage__sellingPoints__point">
            <div className="MysteryBundlePage__sellingPoints__point__icon">
              <FontAwesomeIcon icon={faThumbsUp} />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__body">
              <div className="MysteryBundlePage__sellingPoints__point__title">
                <FormattedMessage id="mystery-bundle-page.selling-point-challenger.two.title" defaultMessage="Very Positive" />
              </div>
              <div className="MysteryBundlePage__sellingPoints__point__text">
                <FormattedMessage id="mystery-bundle-page.selling-point-challenger.two.text" defaultMessage="Average Steam Rating" />
              </div>
            </div>
          </div>

          <div className="MysteryBundlePage__sellingPoints__point">
            <div className="MysteryBundlePage__sellingPoints__point__icon">
              <FontAwesomeIcon icon={fabFaSteam} />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__body">
              <div className="MysteryBundlePage__sellingPoints__point__title">
                <FormattedMessage id="mystery-bundle-page.selling-point-challenger.three.title" defaultMessage="Guaranteed Steam Keys" />
              </div>
              <div className="MysteryBundlePage__sellingPoints__point__text">
                {isBelowMd ? (
                  <FormattedMessage id="mystery-bundle-page.selling-point-challenger.three.mobile.text" defaultMessage="100% offically licensed" />
                ) : (
                  <FormattedMessage id="mystery-bundle-page.selling-point-challenger.three.text" defaultMessage="100% offically licensed games" />
                )}
              </div>
            </div>
          </div>

          <div className="MysteryBundlePage__sellingPoints__point">
            <div className="MysteryBundlePage__sellingPoints__point__icon">
              <FontAwesomeIcon icon={faTag} />
            </div>
            <div className="MysteryBundlePage__sellingPoints__point__body">
              <div className="MysteryBundlePage__sellingPoints__point__title">
                <FormattedMessage id="mystery-bundle-page.selling-point-challenger.four.title" defaultMessage="Get 5% off your next order" />
              </div>
              <div className="MysteryBundlePage__sellingPoints__point__text">
                <FormattedMessage id="mystery-bundle-page.selling-point-challenger.four.text" defaultMessage="Applied after purchase" />
              </div>
            </div>
          </div>
        </section>
      )}

      <TemplateSelectorGameBundle
        product={product}
        bundleTiers={bundleTiers}
      />

      {useNewComponents &&
        <>
          <section className="MysteryBundlePage__step">
            <div className="MysteryBundlePage__step__details">
              <div className="MysteryBundlePage__step__details__pin">
                <FormattedMessage id="mystery-bundle-page.step.one.pin" defaultMessage="Prepare for Gaming Thrills!" />
              </div>
              <h2 className="MysteryBundlePage__step__details__heading">
                <FormattedMessage id="mystery-bundle-page.step.one.heading" defaultMessage="Get ready for the ultimate gaming surprise" />
              </h2>
              <div className="MysteryBundlePage__step__details__text">
                <FormattedMessage
                  id="mystery-bundle-page.step.one.text"
                  defaultMessage="Embark on an exhilarating journey through mystery with Fanatical's exclusive {name}, offering highly rated titles, AAA games and hidden indie gems. What will you uncover with your bundle today?"
                  values={{ name }}
                />
              </div>
            </div>

            <div className="MysteryBundlePage__step__image">
              {topImage &&
                <img
                  srcSet={`
                  ${env.cdnUrl}/product/original/${topImage}?${imgixConfig}&w=1000&h=712&fit=crop,
                  ${env.cdnUrl}/product/original/${topImage}?${imgixConfig}&w=750&h=575&fit=crop,
                  ${env.cdnUrl}/product/original/${topImage}?${imgixConfig}&w=500&h=356&fit=crop
                  ${env.cdnUrl}/product/original/${topImage}?${imgixConfig}&w=250&h=192&fit=crop
                `}
                  src={`${env.cdnUrl}/product/original/${topImage}?${imgixConfig}&w=500&h=356&fit=crop`}
                  alt="Get ready for the ultimate gaming surprise"
                />
              }
            </div>
          </section>

          <section className="MysteryBundlePage__step MysteryBundlePage__step--reverse">
            <div className="MysteryBundlePage__step__details">
              <div className="MysteryBundlePage__step__details__pin">
                <FormattedMessage id="mystery-bundle-page.step.two.pin" defaultMessage="Maximise savings, boost wins" />
              </div>
              <h2 className="MysteryBundlePage__step__details__heading">
                <FormattedMessage id="mystery-bundle-page.step.two.heading" defaultMessage="The more games you buy, the more you save" />
              </h2>
              <div className="MysteryBundlePage__step__details__text">
                <FormattedMessage
                  id="mystery-bundle-page.step.two.text"
                  defaultMessage="Selecting more mystery games not only saves you more, but also boosts your chance to win a Star Prize! Pick your 20-game package to get the most gaming for your money and a chance to win one of our Star Prizes."
                />
              </div>
            </div>

            <div className="MysteryBundlePage__step__image">
              <img
                srcSet={`
                  https://cdn.fanatical.com/production/assets/mystery/landing-page/mystery-template-step-boxes.png?${imgixConfig}&w=1000&h=712&fit=crop,
                  https://cdn.fanatical.com/production/assets/mystery/landing-page/mystery-template-step-boxes.png?${imgixConfig}&w=750&h=575&fit=crop,
                  https://cdn.fanatical.com/production/assets/mystery/landing-page/mystery-template-step-boxes.png?${imgixConfig}&w=500&h=356&fit=crop
                  https://cdn.fanatical.com/production/assets/mystery/landing-page/mystery-template-step-boxes.png?${imgixConfig}&w=250&h=192&fit=crop
                `}
                src={`https://cdn.fanatical.com/production/assets/mystery/landing-page/mystery-template-step-boxes.png?${imgixConfig}&w=500&h=356&fit=crop`}
                alt="The more games you buy, the more you save"
              />
            </div>
          </section>

          <section className="MysteryBundlePage__step">
            <div className="MysteryBundlePage__step__details">
              <div className="MysteryBundlePage__step__details__pin">
                <FormattedMessage id="mystery-bundle-page.step.three.pin" defaultMessage="Reveal, redeem, play" />
              </div>
              <h2 className="MysteryBundlePage__step__details__heading">
                <FormattedMessage id="mystery-bundle-page.step.three.heading" defaultMessage="Instantly reveal your game, redeem it on Steam and get gaming!" />
              </h2>
              <div className="MysteryBundlePage__step__details__text">
                <FormattedMessage
                  id="mystery-bundle-page.step.three.text"
                  defaultMessage="It couldn't be easier to add your new games to your Steam account and start gaming. Everyone's a winner with our mystery bundles so it's time to complete your purchase and reveal the awesome new games you have found."
                />
              </div>
            </div>

            <div className="MysteryBundlePage__step__image">
              <img
                srcSet={`
                  https://cdn.fanatical.com/production/assets/mystery/landing-page/mystery-template-step-star.png?${imgixConfig}&w=1000&h=712&fit=crop,
                  https://cdn.fanatical.com/production/assets/mystery/landing-page/mystery-template-step-star.png?${imgixConfig}&w=750&h=575&fit=crop,
                  https://cdn.fanatical.com/production/assets/mystery/landing-page/mystery-template-step-star.png?${imgixConfig}&w=500&h=356&fit=crop
                  https://cdn.fanatical.com/production/assets/mystery/landing-page/mystery-template-step-star.png?${imgixConfig}&w=250&h=192&fit=crop
                `}
                src={`https://cdn.fanatical.com/production/assets/mystery/landing-page/mystery-template-step-star.png?${imgixConfig}&w=500&h=356&fit=crop`}
                alt="Instantly reveal your game, redeem it on Steam and get gaming!"
              />
            </div>
          </section>
        </>
      }

      {useNewComponents &&
        <section className="MysteryBundlePage__footer">
          <div className="MysteryBundlePage__footer__heading">
            <FormattedMessage
              id="mystery-bundle-page.footer.heading"
              defaultMessage="Surprise yourself - grab a mystery key!"
            />
          </div>

          {templateType !== 'standard_mystery' &&
            <div className="MysteryBundlePage__footer__selection">
              <FormattedMessage
                id="mystery-bundle-page.footer.selection"
                defaultMessage="Select the number of keys you want to buy"
              />
            </div>
          }

          {templateType === 'tiered_mystery' &&
            <MysteryKeySelectionButtons bundles={bundles} selectTier={selectTier} />
          }

          <ProductAddToCartBtn showStickyHeader={false} product={product} />
        </section>
      }

      <ProductOffers
        product={product}
        title={formatMessage(messages.offers_title)}
      />

      <section className="MysteryBundlePage__description">
        <h2>
          <FormattedMessage id="mystery-bundle-page.about-this" defaultMessage="About this Bundle" tagName="span" />
        </h2>
        <DangerousHTML
          html={description}
        />
      </section>

      <section>
        <ProductRecommendations
          product={product}
          title={formatMessage(messages.rec_header)}
        />
      </section>

      {useNewComponents && smallprint &&
        <section className="MysteryBundlePage__smallPrint">
          {smallprint}
        </section>
      }
    </main>
  );
};

MysteryBundleView.propTypes = {
  product: PropTypes.shape({}),
};

MysteryBundleView.defaultProps = {
  product: {},
};

export default MysteryBundleView;

